// ============================================================================
// GENERAL CONSTANTS
// ============================================================================

export const PREFIX = {
    sc: 'sc',
    imp: 'imp',
    bmi: 'bmi',
    sal: 'sal',
};

export const SC_ID = {
    newScenarioId: '@newScenarioID',
    existingScenarioId: '@scenarioId'
};

export const TABLES = {
    SCENARIO: 'cmcdr.cost_model_scenario',
    IMPACTS: 'cmcdr.cost_model_impacts',
    BMI: 'cmcdr.base_material_index_scenario',
    SAL: 'cmcdr.scenario_supplier_allocation'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getFieldsForTable(entityMapper, tableName) {
    return entityMapper?.[tableName]?.fields || [];
}

function getColumn(entityMapper, tableName, field) {
    return entityMapper?.[tableName]?.byField[field] || field;
}

// ============================================================================
// TRANSACTION WRAPPER TEMPLATES
// ============================================================================

export const TransactionTemplates = {
    wrapper: `BEGIN
                DECLARE @newScenarioID INT;
                BEGIN TRY
                  BEGIN TRANSACTION;`,

    end: `END TRY
                BEGIN CATCH
                    IF @@TRANCOUNT > 0
                        ROLLBACK TRANSACTION;

                    DECLARE 
                        @ErrMsg NVARCHAR(4000) = ERROR_MESSAGE(),
                        @ErrSeverity INT = ERROR_SEVERITY(),
                        @ErrState INT = ERROR_STATE();

                    RAISERROR(@ErrMsg, 16, 1);

                    THROW;
                END CATCH;
            END`
};


// ============================================================================
// SCENARIO TEMPLATES
// ============================================================================

export const ScenarioTemplates = {
    insertNew: (entityMapper) => {
        const table = TABLES.SCENARIO;
        const fields = getFieldsForTable(entityMapper, table).filter(f => f !== 'scenarioId');
        const columns = fields.map(f => getColumn(entityMapper, table, f)).join(', ');
        const params = fields.map(f => `:${PREFIX.sc}_${f}`).join(', ');
        
        return `INSERT INTO ${table} (${columns}) VALUES (${params});
                    SET ${SC_ID.newScenarioId} = SCOPE_IDENTITY();
                    SELECT ${SC_ID.newScenarioId} AS scenarioId;`;
    },
    
    updateExisting: (entityMapper) => {
        const table = TABLES.SCENARIO;
        const fields = getFieldsForTable(entityMapper, table).filter(f => f !== 'scenarioId');
        const setClause = fields.map(f => `  ${getColumn(entityMapper, table, f)} = :${PREFIX.sc}_${f}`).join(',\n');
        return `DECLARE ${SC_ID.existingScenarioId} INT = :scenarioId;
                    UPDATE ${table} SET
                    ${setClause}
                    WHERE scenario_id = ${SC_ID.existingScenarioId};
                    UPDATE cmcdr.cost_model_impacts SET obsolete_flag = 'X' WHERE scenario_id = ${SC_ID.existingScenarioId};
                    UPDATE cmcdr.results_inputs SET obsolete_flag = 'X' WHERE scenario_id = ${SC_ID.existingScenarioId};
                    UPDATE cmcdr.results_prices SET obsolete_flag = 'X' WHERE scenario_id = ${SC_ID.existingScenarioId};`;
    }
};

// ============================================================================
// IMPACT TEMPLATES
// ============================================================================

export const ImpactTemplates = {
    build(isNewScenario, impactsCount, entityMapper) {
        if (impactsCount === 0) return '';

        const table = TABLES.IMPACTS;
        const scenarioRef = isNewScenario ? SC_ID.newScenarioId : SC_ID.existingScenarioId;
        const fields = getFieldsForTable(entityMapper, table).filter(f => f !== 'scenarioId');
        const columns = fields.map(f => getColumn(entityMapper, table, f)).join(', ');
        
        const values = Array(impactsCount)
            .fill(0)
            .map((_, i) => {
                const prefix = `${PREFIX.imp}_${i}`;
                return `(${scenarioRef}, ${fields.map(f => `:${prefix}_${f}`).join(', ')})`;
            })
            .join(',\n    ');

        return `INSERT INTO ${table} (scenario_id, ${columns}) VALUES\n    ${values};`;
    }
};

// ============================================================================
// BMI TEMPLATES
// ============================================================================

export const BMITemplates = {
    build(isNewScenario, bmiCount, entityMapper) {
        if (bmiCount === 0) return '';

        const table = TABLES.BMI;
        const scenarioRef = isNewScenario ? SC_ID.newScenarioId : SC_ID.existingScenarioId;
        const fields = getFieldsForTable(entityMapper, table).filter(f => f !== 'scenarioId');

        return Array(bmiCount)
            .fill(0)
            .map((_, i) => {
                const prefix = `${PREFIX.bmi}_${i}`;
                
                // Add type declarations
                const decls = fields.map(f => {
                    const type = 'NVARCHAR(MAX)';  // Get type based on field
                    return `@${prefix}_${f} ${type} = :${prefix}_${f}`;
                }).join(',\n                        ');

                return `
                    DECLARE
                        ${decls};

                    ;MERGE ${table} AS target
                    USING (
                        SELECT ${scenarioRef} AS scenario_id, @${prefix}_cmKey AS cost_model_key, @${prefix}_bm AS bm
                    ) AS src
                    ON (target.scenario_id = src.scenario_id AND target.cost_model_key = src.cost_model_key AND target.bm = src.bm)
                    WHEN MATCHED THEN UPDATE SET [index] = @${prefix}_indexName, overwrite_index = @${prefix}_overwriteIndex, flag = @${prefix}_flag, modified_by = @${prefix}_modifiedBy, last_updated = @${prefix}_lastUpdated
                    WHEN NOT MATCHED BY TARGET THEN INSERT (scenario_id, cost_model_id, cost_model_key, bm, [index], overwrite_index, flag, created_by, modified_by, last_updated) VALUES (${scenarioRef}, ${fields.map(f => `@${prefix}_${f}`).join(', ')});`.trim();
            })
            .join('\n');
    }
};

// ============================================================================
// SAL TEMPLATES
// ============================================================================

export const SALTemplates = {
    build(isNewScenario, salCount, entityMapper) {
        if (salCount === 0) return '';

        const table = TABLES.SAL;
        const scenarioRef = isNewScenario ? SC_ID.newScenarioId : SC_ID.existingScenarioId;
        const fields = getFieldsForTable(entityMapper, table).filter(f => f !== 'scenarioId');
        const columns = fields.map(f => getColumn(entityMapper, table, f)).join(', ');

        let sql = '';

        if (!isNewScenario) {
            sql += `UPDATE ${table} SET obsolete_flag = 'X' WHERE scenario_id = ${scenarioRef};\n`;
        }

        const declares = Array(salCount)
            .fill(0)
            .map((_, i) => {
                const prefix = `${PREFIX.sal}_${i}`;
                // Add type declarations
                const decls = fields.map(f => {
                    const type = 'NVARCHAR(MAX)';
                    return `@${prefix}_${f} ${type} = :${prefix}_${f}`;
                }).join(',\n                        ');

                return `
                    DECLARE
                        ${decls};`;
            })
            .join('\n');

        sql += declares;

        const values = Array(salCount)
            .fill(0)
            .map((_, i) => {
                const prefix = `${PREFIX.sal}_${i}`;
                return `(${scenarioRef}, ${fields.map(f => `@${prefix}_${f}`).join(', ')})`;
            })
            .join(',\n    ');

        sql += `\n\nINSERT INTO ${table} (scenario_id, ${columns}) VALUES\n    ${values};`;

        return sql;
    }
};

export const bindScenarioParams = (bindings, data, entityMapper) => {
    console.log(bindings, data, entityMapper)
    getFieldsForTable(entityMapper, TABLES.SCENARIO)
        .filter(f => f !== 'scenarioId')
        .forEach(field => {
            if (data[field] !== undefined) {
                bindings.set(`${PREFIX.sc}_${field}`, data[field]);
            } else if (field === 'originalScenarioId') {
                // Always set originalScenarioId to null if not provided to match SQL expectations
                bindings.set(`${PREFIX.sc}_${field}`, null);
            }
        });
};

export const bindImpactsParams = (bindings, data, entityMapper) => {
    const fields = getFieldsForTable(entityMapper, TABLES.IMPACTS).filter(f => f !== 'scenarioId');
    data.forEach((record, i) => {
        fields.forEach(field => {
            bindings.set(`${PREFIX.imp}_${i}_${field}`, record[field]);
        });
    });
};

export const bindBMIParams = (bindings, data, entityMapper) => {
    const fields = getFieldsForTable(entityMapper, TABLES.BMI);
    data.forEach((record, i) => {
        fields.forEach(field => {
            bindings.set(`${PREFIX.bmi}_${i}_${field}`, record[field]);
        });
    });
};

export const bindSALParams = (bindings, data, entityMapper) => {
    const fields = getFieldsForTable(entityMapper, TABLES.SAL);
    data.forEach((record, i) => {
        fields.forEach(field => {
            bindings.set(`${PREFIX.sal}_${i}_${field}`, record[field]);
        });
    });
};

export const bindScenarioId = (bindings, data) => bindings.set('scenarioId', data.scenarioId);





